"use client";

import React from "react";
import { Spinner, Chip } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";

import { ModalOverlay, ModalHeader, ModalBody } from "@/components/UI/ModalOverlay";
import { documentCache } from "@/utils/documentCache";
import {
  Citation,
  getGoogleDriveWebViewLink,
  getSourceWebLink,
  isConfluenceCitation,
  isGoogleDriveCitation,
  isSharePointCitation,
} from "@/types/citations";
import {
  detectDocumentType,
  detectDocumentTypeFromMimeAndFilename,
  DocumentType,
} from "@/utils/fileUtils";
import { useAuthenticatedFileFetcher } from "@/hooks/chat/useAuthenticatedFileFetcher";
import { logger } from "@/telemetry";
import { isSafeUrl } from "@/utils/url";

import { PDFViewer } from "./viewers/PDFViewer";
import { DOCXViewer } from "./viewers/DOCXViewer";
import { PPTXViewer } from "./viewers/PPTXViewer";
import { ImageViewer } from "./viewers/ImageViewer";
import { TXTViewer } from "./viewers/TXTViewer";
import { CSVViewer } from "./viewers/CSVViewer";
import { ExcelViewer } from "./viewers/ExcelViewer";
import { MarkdownViewer } from "./viewers/MarkdownViewer";

/**
 * Sniff the first bytes of an ArrayBuffer to detect the actual file format,
 * regardless of what the server Content-Type header claims. This catches cases
 * where Google Workspace files are stored as PDF exports but served with the
 * original Google mimeType (e.g. application/vnd.google-apps.document).
 */
function detectTypeFromBytes(buffer: ArrayBuffer): DocumentType | null {
  if (buffer.byteLength < 8) return null;
  const header = new Uint8Array(buffer.slice(0, 8));

  // PDF: %PDF-
  if (
    header[0] === 0x25 &&
    header[1] === 0x50 &&
    header[2] === 0x44 &&
    header[3] === 0x46 &&
    header[4] === 0x2d
  )
    return "pdf";
  // DOCX/XLSX/PPTX (ZIP PK header): PK\x03\x04
  if (header[0] === 0x50 && header[1] === 0x4b && header[2] === 0x03 && header[3] === 0x04)
    return null; // Let mimeType-based detection handle which Office format

  return null;
}

export interface CitationDocumentViewerProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  citation: Citation | null;
}

/**
 * Modal component for viewing citations in their original documents
 * Downloads document, renders with appropriate viewer (PDF/DOCX), and highlights citation text
 */
export const CitationDocumentViewer: React.FC<CitationDocumentViewerProps> = ({
  isOpen,
  onOpenChange,
  citation,
}) => {
  const { t } = useTranslation();
  const fileFetcher = useAuthenticatedFileFetcher();
  const [documentData, setDocumentData] = React.useState<ArrayBuffer | null>(null);
  const [documentType, setDocumentType] = React.useState<DocumentType>("unknown");

  /**
   * Detect document type using filename, mimeType from citation metadata, and Google Drive heuristics.
   * Google Workspace files are exported as PDF during backfill, so they render via PDFViewer.
   * Falls back to markdown viewer if PDF export was unavailable.
   */
  const detectType = React.useCallback(
    (filename: string, mimeType?: string | null, data?: ArrayBuffer | null): DocumentType => {
      // Check actual bytes first — most reliable for PDFs served with wrong Content-Type
      if (data) {
        const byBytes = detectTypeFromBytes(data);

        if (byBytes) return byBytes;
      }

      // Try extension-based detection
      const byExtension = detectDocumentType(filename);

      if (byExtension !== "unknown") return byExtension;

      // Try mimeType-based detection (handles Google Workspace → "markdown", standard types, etc.)
      if (mimeType) {
        const byMime = detectDocumentTypeFromMimeAndFilename(mimeType, filename);

        if (byMime && byMime !== "unknown") return byMime;
      }

      // For Google Drive citations without recognisable extension/mime,
      // default to markdown as a safe fallback (normally these are exported as PDF).
      // Confluence and SharePoint citations are indexed as plain text without a
      // file backing, so render the citation chunk as markdown.
      if (
        citation &&
        (isGoogleDriveCitation(citation) ||
          isConfluenceCitation(citation) ||
          isSharePointCitation(citation))
      )
        return "markdown";

      return "unknown";
    },
    [citation],
  );

  // Download document when citation changes
  // For Google Drive files, try PDF preview first for better rendering + highlighting
  React.useEffect(() => {
    if (!citation || !isOpen) return;

    const downloadDocument = async () => {
      // Check cache first
      const cacheKey = citation.document_id;
      const cached = documentCache.get(cacheKey);

      if (cached) {
        logger.info("Using cached document", { documentId: citation.document_id });
        const arrayBuffer = new ArrayBuffer(cached.data.byteLength);
        const view = new Uint8Array(arrayBuffer);

        view.set(cached.data);
        setDocumentData(arrayBuffer);
        setDocumentType(
          detectType(
            citation.filename,
            cached.mimeType || citation.metadata?.mimeType,
            arrayBuffer,
          ),
        );

        return;
      }

      // Download the file (Google Drive files are already stored as PDF by backfill)
      logger.info("Downloading document", { documentId: citation.document_id });
      await fileFetcher.fetchFile(citation.document_id, citation.filename, {
        asArrayBuffer: true,
      });
    };

    downloadDocument();
    // TODO: Stabilise fileFetcher ref so the full dependency array can be used
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fileFetcher is an unstable object ref; citation and isOpen are the real triggers
  }, [citation, isOpen]);

  // Handle fetched data: cache and set state
  React.useEffect(() => {
    if (fileFetcher.data.arrayBuffer && citation) {
      // IMPORTANT: Cache FIRST before PDF.js can detach the ArrayBuffer
      // The cache.set() converts it to Uint8Array immediately
      documentCache.set(
        citation.document_id,
        fileFetcher.data.arrayBuffer,
        undefined,
        fileFetcher.data.mimeType || undefined,
      );

      // Now get it back from cache as a fresh ArrayBuffer
      const cachedDoc = documentCache.get(citation.document_id);

      if (cachedDoc) {
        const freshArrayBuffer = new ArrayBuffer(cachedDoc.data.byteLength);
        const view = new Uint8Array(freshArrayBuffer);

        view.set(cachedDoc.data);
        setDocumentData(freshArrayBuffer);
        setDocumentType(
          detectType(
            citation.filename,
            fileFetcher.data.mimeType || citation.metadata?.mimeType,
            freshArrayBuffer,
          ),
        );

        logger.info("✅ Document downloaded", {
          sizeKB: (fileFetcher.data.arrayBuffer.byteLength / 1024).toFixed(1),
        });
      } else {
        // Fallback (should never happen)
        setDocumentData(fileFetcher.data.arrayBuffer);
        setDocumentType(
          detectType(
            citation.filename,
            fileFetcher.data.mimeType || citation.metadata?.mimeType,
            fileFetcher.data.arrayBuffer,
          ),
        );
      }
    }
  }, [fileFetcher.data.arrayBuffer, citation, fileFetcher.data.mimeType, detectType]);

  // Reset state when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setDocumentData(null);
      fileFetcher.cleanup();
    }
    // TODO: Stabilise fileFetcher ref so the full dependency array can be used
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fileFetcher is an unstable object ref; isOpen is the real trigger
  }, [isOpen]);

  /**
   * Icon and labels for a citation's source system, used by the external-link
   * affordance. Keyed off the stable source_service via the citation helpers so
   * every known source (Confluence, SharePoint, Google Drive) is handled, and
   * any other source still gets a generic "open link" rather than being dropped.
   */
  const sourcePresentation = (
    c: Citation | null,
  ): { icon: string; linkLabel: string; headerLabel: string } => {
    if (isConfluenceCitation(c)) {
      return {
        icon: "logos:confluence",
        linkLabel: t("Open in Confluence"),
        headerLabel: t("Confluence Page"),
      };
    }
    if (isSharePointCitation(c)) {
      return {
        icon: "logos:microsoft-sharepoint",
        linkLabel: t("Open in SharePoint"),
        headerLabel: t("SharePoint Document"),
      };
    }
    if (isGoogleDriveCitation(c)) {
      return {
        icon: "logos:google-drive",
        linkLabel: t("Open in Google Drive"),
        headerLabel: t("Google Drive File"),
      };
    }

    return {
      icon: "solar:link-linear",
      linkLabel: t("Open link"),
      headerLabel: t("Unsupported document type"),
    };
  };

  const renderViewer = () => {
    if (fileFetcher.loading) {
      return (
        <div className="flex items-center justify-center h-[60vh]">
          <Spinner label={t("Loading document...") || "Loading document..."} />
        </div>
      );
    }

    if (fileFetcher.error) {
      return (
        <div className="flex flex-col items-center justify-center h-[60vh] gap-4">
          <Icon className="text-danger-500" icon="solar:danger-circle-linear" width={48} />
          <p className="text-danger-500">
            {t("Failed to load document. Please try again.") || fileFetcher.error}
          </p>
          {citation && (
            <div className="bg-default-100 dark:bg-default-100/50 p-4 rounded-medium max-w-2xl">
              <p className="text-sm font-medium mb-2 text-default-800 dark:text-white">
                Citation text:
              </p>
              <p className="text-xs text-default-600 dark:text-default-300">{citation.text}</p>
            </div>
          )}
        </div>
      );
    }

    if (!documentData || !citation) {
      return (
        <div className="flex items-center justify-center h-[60vh]">
          <p className="text-default-500 dark:text-default-400">No document to display</p>
        </div>
      );
    }

    if (documentType === "pdf") {
      return <PDFViewer citationText={citation.text} documentData={documentData} />;
    }

    if (documentType === "docx") {
      return <DOCXViewer citationText={citation.text} documentData={documentData} />;
    }

    if (documentType === "pptx") {
      return <PPTXViewer citationText={citation.text} documentData={documentData} />;
    }

    if (documentType === "image") {
      return <ImageViewer documentData={documentData} filename={citation.filename} />;
    }

    if (documentType === "txt") {
      return <TXTViewer citationText={citation.text} documentData={documentData} />;
    }

    if (documentType === "markdown") {
      // Confluence and SharePoint are text-indexed sources rendered as markdown;
      // both expose their original page via the generic source_web_link.
      const isExternalTextSource = isConfluenceCitation(citation) || isSharePointCitation(citation);
      const rawSourceLink = isExternalTextSource ? getSourceWebLink(citation) : undefined;
      const sourceLink = isSafeUrl(rawSourceLink) ? rawSourceLink : undefined;

      if (sourceLink) {
        const { icon, linkLabel } = sourcePresentation(citation);

        return (
          <div className="flex flex-col h-full">
            <div className="px-4 py-2 border-b border-divider flex justify-end shrink-0">
              <a
                className="text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-2 text-sm"
                data-id="citation-external-source-link"
                href={sourceLink}
                rel="noopener noreferrer"
                target="_blank"
              >
                <Icon aria-hidden="true" icon={icon} width={18} />
                {linkLabel}
              </a>
            </div>
            <div className="flex-1 min-h-0 overflow-auto">
              <MarkdownViewer documentData={documentData} />
            </div>
          </div>
        );
      }

      return <MarkdownViewer documentData={documentData} />;
    }

    if (documentType === "csv") {
      return (
        <CSVViewer
          citationText={citation.text}
          documentData={documentData}
          filename={citation.filename}
        />
      );
    }

    if (documentType === "excel") {
      return (
        <ExcelViewer
          citationText={citation.text}
          documentData={documentData}
          filename={citation.filename}
        />
      );
    }

    // This fallback renders when the document cannot be shown inline (e.g. an
    // unsupported binary type). If the source system exposes a safe external
    // link, offer it so the user can still open the original document. Google
    // Drive keeps its legacy field; every other source uses the generic getter.
    const {
      icon: sourceIcon,
      linkLabel: sourceLinkLabel,
      headerLabel: sourceHeaderLabel,
    } = sourcePresentation(citation);
    const rawExternalLink = isGoogleDriveCitation(citation)
      ? getGoogleDriveWebViewLink(citation)
      : getSourceWebLink(citation);
    const externalLink = isSafeUrl(rawExternalLink) ? rawExternalLink : undefined;

    return (
      <div className="flex flex-col items-center justify-center h-[60vh] gap-4">
        <Icon
          aria-hidden="true"
          className="text-warning-500"
          icon="solar:file-text-linear"
          width={48}
        />
        <p className="text-default-600 dark:text-default-300">{sourceHeaderLabel}</p>
        {externalLink ? (
          <>
            <a
              className="text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-2"
              data-id="citation-external-source-link"
              href={externalLink}
              rel="noopener noreferrer"
              target="_blank"
            >
              <Icon aria-hidden="true" icon={sourceIcon} width={18} />
              {sourceLinkLabel}
            </a>
            {citation?.text && (
              <div className="bg-default-100 dark:bg-default-100/50 p-4 rounded-medium max-w-2xl mt-4">
                <p className="text-sm font-medium mb-2 text-default-800 dark:text-white">
                  Citation text:
                </p>
                <p className="text-xs text-default-600 dark:text-default-300 whitespace-pre-wrap">
                  {citation.text}
                </p>
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-default-500 dark:text-default-400">
            Only PDF, DOCX, PPTX, TXT, Markdown, CSV/TSV, Excel, and image files are supported
          </p>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay
      ariaLabelledBy="citation-modal-title"
      className="citation-document-viewer-modal"
      isOpen={isOpen}
      scrollBehavior="inside"
      size="5xl"
      onOpenChange={onOpenChange}
    >
      <ModalHeader
        className="flex items-center gap-3 pr-12 border-b border-divider"
        data-id="citation-modal-title"
      >
        <Icon className="text-primary shrink-0" icon="solar:book-bookmark-linear" width={24} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {citation && (
              <Chip size="sm" variant="secondary">
                {citation.label}
              </Chip>
            )}
            <span className="text-base font-semibold truncate">
              {citation?.filename || "Citation"}
            </span>
          </div>
        </div>
      </ModalHeader>

      <ModalBody className="flex-1 min-h-0 overflow-hidden p-0">
        <div aria-label="Document viewer" className="h-full" role="document">
          {renderViewer()}
        </div>
      </ModalBody>
    </ModalOverlay>
  );
};

export default CitationDocumentViewer;
